/**
 * PnLTable — bảng tái sử dụng cho TẤT CẢ bảng dashboard Commercial Position
 * (theo `plan/PBI_Dashboard.md` §7). Dày đặc, font nhỏ; tự format số/tiền, tô đỏ +
 * ngoặc cho số âm, zebra, in đậm hàng `isTotal`. Hỗ trợ:
 *  - `headerGroups`: header gộp 2 tầng (bảng Long-Short).
 *  - `cellHighlight`: callback trả màu nền 1 ô (ô cảnh báo Supplier M2M).
 *  - `height`: height CỐ ĐỊNH + scroll dọc trong card + header sticky (bảng
 *    Supplier dài; các block cùng hàng grid set cùng height cho đều nhau).
 *    Dòng `isTotal` pin sticky ĐÁY vùng scroll — Total luôn thấy không cần
 *    cuộn hết bảng (Futures/Supplier M2M, giống grand total PBI).
 *
 * Dùng MUI (theo convention project), KHÔNG Tailwind/tanstack như doc gợi ý —
 * doc chỉ định hướng UI, cấu trúc bám project.
 */
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { useMemo } from 'react'

import { fmtDate, fmtNum } from './format'
import { titleCaseName } from '../../../utils/text'
import { PNL_COLORS } from './tokens'

export interface PnLColumn {
  key: string
  label: string
  align?: 'left' | 'right'
  /** Cột tiền USD → prefix `$`. */
  currency?: boolean
  /** Cột text (không format số) — mặc định cột đầu là text. */
  text?: boolean
  /** Title-case the cell value (proper names stored UPPERCASE in DB). Display-only. */
  proper?: boolean
  /** Cột ngày → format `DD-MMM-YYYY` (Access parity). */
  date?: boolean
  /** Min-width cột (px) — nới cột bị table-layout auto bóp hẹp (vd Grade Long-Short). */
  width?: number
  /** Giữ nguyên leading spaces (whiteSpace pre) — label thụt cấp kiểu PBI
   *  (vd USD exposure dashboard Cashflows). */
  pre?: boolean
  /** Giữ dấu trừ thay vì ngoặc (cột Change). */
  signed?: boolean
  decimals?: number
  /** Data bar trong cell (PBI tableEx `dataBars`, vd Total exp Supplier M2M):
   *  trục neo tại 0 ở mép PHẢI (PBI `maxValue: 0`) — chỉ giá trị ÂM vẽ bar
   *  `negativeColor` chạy sang trái, scale theo |min| của cột; số hiện đè trên
   *  bar (`hideText: false`), chữ giữ màu mặc định (PBI không CF font cột này). */
  dataBars?: { negativeColor: string }
}

/** Header tầng trên (gộp ô) — span = số cột con bên dưới. */
export interface PnLHeaderGroup {
  label: string
  span: number
}

export type PnLRow = Record<string, string | number | boolean | null | undefined> & {
  isTotal?: boolean
}

interface PnLTableProps {
  columns: PnLColumn[]
  rows: PnLRow[]
  headerGroups?: PnLHeaderGroup[]
  /** Height cố định (px / CSS string / responsive object — vd `{ xs: 460, lg: '100%' }`):
   *  bảng dài scroll dọc + sticky header; bảng ngắn giữ nguyên height (card đều
   *  nhau trong grid row). `'100%'` để fill card bounded (fit-screen layout). */
  height?: number | string | Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number | string>>
  /** Bảng NGẮN stretch 100% height container (card grid cao hơn): khác `height`
   *  (scroll), `fill` set height 100% cho cả `<Table>` → phần dư chia đều vào
   *  từng dòng (vd Credit Facilities dashboard Cashflows). */
  fill?: boolean
  /** (row, colKey) → màu nền ô (cảnh báo). undefined = không tô. */
  cellHighlight?: (row: PnLRow, colKey: string) => string | undefined
  /** Override cỡ chữ header / body (vd Futures Position dùng size Cashflows
   *  '0.66rem'/'0.68rem'). Bỏ trống = giữ size mặc định responsive. */
  headFontSize?: string
  bodyFontSize?: string
}

/** Header row theo template /table-style (`app-table-header-cell`): gradient
 *  cream-gold + chữ teal 900 + border-bottom đậm. Gradient opaque → sticky
 *  header không bleed-through khi scroll dọc.
 *  Label giữ 1 LINE (nowrap, chị Đẹp yêu cầu 2026-06-10) — bù lại font nhỏ
 *  (0.6rem) + padding hẹp để 6-7 cột vẫn fit card 3-cột; card overflow:auto
 *  là fallback nếu viewport quá hẹp. */
const HEAD_SX = {
  bgcolor: PNL_COLORS.headerBg,
  color: PNL_COLORS.headerText,
  fontWeight: 600,
  fontSize: '0.66rem',
  lineHeight: 1.15,
  py: 0.5,
  px: { xs: 0.5, xl: 0.75 },
  border: 0,
  borderBottom: `1px solid ${PNL_COLORS.headerBg}`,
  whiteSpace: 'nowrap',
} as const

export default function PnLTable({ columns, rows, headerGroups, height, fill, cellHighlight, headFontSize, bodyFontSize }: PnLTableProps) {
  // Per-instance font override (default = responsive HEAD_SX / body sizes).
  const headSx = headFontSize ? { ...HEAD_SX, fontSize: headFontSize } : HEAD_SX
  const bodyFontSx = bodyFontSize ?? '0.68rem'
  // Scale data bar per cột (PBI `dataBars` maxValue=0): |min âm| của cột = bar
  // 100%; dòng Total không tính vào scale và không vẽ bar.
  const dataBarScale = useMemo(() => {
    const m = new Map<string, number>()
    for (const c of columns) {
      if (!c.dataBars) continue
      let maxAbs = 0
      for (const r of rows) {
        if (r.isTotal) continue
        const v = Number(r[c.key])
        if (Number.isFinite(v) && v < 0) maxAbs = Math.max(maxAbs, Math.abs(v))
      }
      if (maxAbs > 0) m.set(c.key, maxAbs)
    }
    return m
  }, [columns, rows])

  return (
    <Box
      sx={{
        // Bảng KHÔNG cap height (Purchases/Sales/M2M PnL/Physical/Exchange…):
        // hiện full, KHÔNG scroll ngang riêng — để toàn report scroll ngang 1 lần
        // (chị Đẹp 2026-06-21). Bảng có cap height (Futures/Supplier M2M/Inventory…)
        // vẫn scroll dọc nội bộ (sticky header) → overflow auto.
        overflow: height || fill ? 'auto' : 'visible',
        ...(height ? { height } : {}),
        ...(fill ? { height: '100%' } : {}),
        border: `1px solid ${PNL_COLORS.border}`,
        borderRadius: '6px',
      }}
    >
      <Table
        size="small"
        stickyHeader={!!height}
        sx={{
          // `separate` + borderSpacing 0 → sticky header cells keep an OPAQUE
          // background while scrolling (collapse mode leaks body rows through →
          // text overlap). Matches CashTable.
          borderCollapse: 'separate',
          borderSpacing: 0,
          // Theme default (`.MuiTableHead-root .MuiTableCell-head`) paints a
          // SEMI-TRANSPARENT gold `alpha(#f4df9c, .18)` → header see-through →
          // body text shows through on scroll. Re-assert the SAME pale gold but
          // OPAQUE (its blend over white = #fdf9ed) at higher specificity, so the
          // look is unchanged (vàng nhạt + dark text) but no longer transparent.
          '& .MuiTableHead-root .MuiTableCell-head': {
            backgroundColor: '#fdf9ed',
            backgroundImage: 'none',
          },
          ...(fill ? { height: '100%' } : {}),
        }}
      >
        <TableHead>
          {headerGroups ? (
            <TableRow>
              {headerGroups.map((g, i) => (
                <TableCell
                  key={`${g.label}-${i}`}
                  align="center"
                  colSpan={g.span}
                  sx={{ ...headSx, top: 0 }}
                >
                  {g.label}
                </TableCell>
              ))}
            </TableRow>
          ) : null}
          <TableRow>
            {columns.map((c) => (
              <TableCell
                key={c.key}
                align={c.align ?? (c.text ? 'left' : 'right')}
                sx={{ ...headSx, top: headerGroups ? 22 : 0, ...(c.width ? { minWidth: c.width } : {}) }}
              >
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, ri) => (
            <TableRow key={ri}>
              {columns.map((c) => {
                const raw = row[c.key]
                // Mặc định cột đầu = text (label); cột đầu là SỐ thì khai
                // `text: false` explicit để qua fmtNum (vd Exchange rate USD/VND).
                const isText = c.text ?? c.key === columns[0].key
                const hl = cellHighlight?.(row, c.key)
                let content: React.ReactNode = ''
                let neg = false
                if (c.date) {
                  content = fmtDate(raw)
                } else if (isText) {
                  content = raw == null ? '' : c.proper ? titleCaseName(raw) : String(raw)
                } else if (raw != null && raw !== '') {
                  const r = fmtNum(Number(raw), {
                    currency: c.currency,
                    decimals: c.decimals,
                    signed: c.signed,
                  })
                  content = r.text
                  neg = r.neg
                }
                // Data bar (PBI maxValue=0): chỉ giá trị ÂM, neo mép phải chạy
                // sang trái; số đè trên bar, chữ màu mặc định cho dễ đọc.
                const barMax = c.dataBars ? dataBarScale.get(c.key) : undefined
                const vNum = Number(raw)
                const barPct =
                  barMax && !row.isTotal && raw != null && raw !== '' && Number.isFinite(vNum) && vNum < 0
                    ? Math.min(100, (Math.abs(vNum) / barMax) * 100)
                    : 0
                return (
                  <TableCell
                    key={c.key}
                    align={c.align ?? (isText ? 'left' : 'right')}
                    sx={{
                      fontSize: bodyFontSx,
                      lineHeight: 1.25,
                      py: 0.35,
                      px: { xs: 0.5, xl: 0.75 },
                      // Số GIỮ nowrap (không được gãy "1,234,567"); cột text
                      // (supplier/label dài) wrap để không ép rộng cột; cột
                      // `pre` giữ leading spaces (label thụt cấp).
                      whiteSpace: c.pre ? 'pre' : isText ? 'normal' : 'nowrap',
                      overflowWrap: isText && !c.pre ? 'anywhere' : undefined,
                      verticalAlign: 'top',
                      // Chỉ kẻ ngang (bỏ viền dọc) — skin Cashflows.
                      borderBottom: `1px solid ${PNL_COLORS.grid}`,
                      // Bảng scroll: dòng Total dính đáy (bg totalRowBg opaque
                      // nên không bleed-through data phía sau khi cuộn).
                      ...(height && row.isTotal
                        ? { position: 'sticky', bottom: 0, zIndex: 2, borderTop: `1px solid ${PNL_COLORS.grid}` }
                        : {}),
                      fontWeight: row.isTotal ? 700 : 400,
                      ...(barPct > 0 ? { position: 'relative' } : {}),
                      bgcolor: hl ? hl : row.isTotal ? PNL_COLORS.totalRowBg : PNL_COLORS.rowBg,
                      // Cell có data bar: chữ giữ màu mặc định (PBI không CF font
                      // cột này — đỏ trên bar đỏ sẽ chìm).
                      color: hl ? '#fff' : neg && barPct === 0 ? PNL_COLORS.negative : PNL_COLORS.positive,
                    }}
                  >
                    {barPct > 0 && c.dataBars ? (
                      <>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 1,
                            bottom: 1,
                            right: 0,
                            width: `${barPct}%`,
                            bgcolor: c.dataBars.negativeColor,
                            opacity: 0.45,
                          }}
                        />
                        <Box component="span" sx={{ position: 'relative' }}>{content}</Box>
                      </>
                    ) : (
                      content
                    )}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}
